import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyDIDs } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import Modal from '../../components/Modal';
import { formatRevocationReason } from '../../lib/revocationReasons';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  revoked: 'bg-red-100 text-red-700',
  expired: 'bg-slate-200 text-slate-700',
  suspended: 'bg-amber-100 text-amber-700',
};

// An expired credential is no longer usable even though it was never revoked,
// so expiry has to be part of the status the holder sees — not just a date
// printed in the details panel. Revocation still wins: a credential revoked
// before it expired should read "revoked".
function isExpired(expirationDate?: string): boolean {
  if (!expirationDate) return false;
  const t = Date.parse(expirationDate);
  return Number.isFinite(t) && t < Date.now();
}

interface RawVC {
  id: string;
  type?: string[];
  issuanceDate?: string;
  expirationDate?: string;
  revoked?: boolean;
  revocationReason?: string;
  revokedAt?: string;
  issuer?: string | { id: string; name?: string };
  credentialSubject?: Record<string, unknown>;
  credentialSchema?: { id?: string };
}

interface CredRow {
  id: string;
  type: string;
  issuer: string; // full issuer DID (truncated only at render time)
  issuedAt: string;
  status: 'active' | 'revoked' | 'expired';
  revocationReason?: string;
  revokedAt?: string;
  raw: RawVC; // full credential for the read-only details view
}

function mapVC(vc: RawVC): CredRow {
  const credType = (vc.type ?? []).find((t) => t !== 'VerifiableCredential') ?? '—';
  const issuerStr = typeof vc.issuer === 'string' ? vc.issuer : (vc.issuer?.id ?? '—');
  return {
    id: vc.id,
    type: credType,
    issuer: issuerStr,
    issuedAt: vc.issuanceDate ?? '',
    status: vc.revoked ? 'revoked' : isExpired(vc.expirationDate) ? 'expired' : 'active',
    revocationReason: vc.revocationReason,
    revokedAt: vc.revokedAt,
    raw: vc,
  };
}

// Middle-truncate a long value for display; the full value stays available via
// the copy button and the title tooltip.
function shorten(value: string) {
  if (value.startsWith('urn:uuid:')) return value.slice(9, 17) + '…';
  if (value.length <= 26) return value;
  return value.slice(0, 12) + '…' + value.slice(-8);
}

function formatDate(iso: string) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

// Shows a middle-truncated value with the full string on hover (title) and a
// copy button that puts the COMPLETE value on the clipboard.
function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '—') return <span className="text-text-faint">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span className="group inline-flex items-center gap-1.5">
      <span className="font-mono text-xs text-text-main" title={value}>{shorten(value)}</span>
      <button
        onClick={copy}
        title={copied ? 'Copied!' : `Copy full value: ${value}`}
        aria-label="Copy full value"
        className="rounded p-0.5 text-text-faint opacity-0 transition-opacity hover:bg-slate-100 hover:text-text-main group-hover:opacity-100 focus:opacity-100"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
          {copied ? 'check' : 'content_copy'}
        </span>
      </button>
    </span>
  );
}

// Read-only credential details — the holder can review everything but edit nothing.
function CredentialDetails({ cred, onClose }: { cred: CredRow; onClose: () => void }) {
  const vc = cred.raw;
  const claims = Object.entries(vc.credentialSubject ?? {}).filter(([k]) => k !== 'id');
  const subjectDid = (vc.credentialSubject?.id as string) ?? '—';
  const issuerName = typeof vc.issuer === 'object' ? vc.issuer?.name : undefined;

  const field = (label: string, value: ReactNode) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-text-faint">{label}</p>
      <div className="mt-0.5 text-sm text-text-main break-all">{value}</div>
    </div>
  );

  return (
    <Modal open onClose={onClose} title="Credential Details" wide>
      <div className="space-y-6">
        {/* Status + revocation reason */}
        <div className="flex items-center gap-3">
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold capitalize ${STATUS_COLORS[cred.status] ?? 'bg-slate-100 text-slate-600'}`}>
            {cred.status}
          </span>
          <span className="text-xs text-text-faint">Read-only view — credentials cannot be modified by the holder.</span>
        </div>
        {cred.status === 'revoked' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined mt-0.5 text-red-600" style={{ fontSize: 18 }}>block</span>
              <div className="text-sm">
                <p className="font-bold text-red-700">This credential has been revoked by the issuer.</p>
                {cred.revocationReason ? (
                  <p className="mt-1 text-red-700"><span className="font-semibold">Reason:</span> {formatRevocationReason(cred.revocationReason)}</p>
                ) : (
                  <p className="mt-1 text-red-600/80">No reason was provided by the issuer.</p>
                )}
                {cred.revokedAt && (
                  <p className="mt-0.5 text-xs text-red-600/80">Revoked on {formatDate(cred.revokedAt)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {cred.status === 'expired' && (
          <div className="rounded-xl border border-slate-300 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined mt-0.5 text-slate-600" style={{ fontSize: 18 }}>schedule</span>
              <div className="text-sm">
                <p className="font-bold text-slate-700">This credential has expired.</p>
                <p className="mt-1 text-slate-600">
                  It expired on {formatDate(vc.expirationDate ?? '')} and is no longer valid for presentation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Core fields */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {field('Credential ID', <span className="font-mono text-xs">{vc.id}</span>)}
          {field('Type', (vc.type ?? []).join(', ') || '—')}
          {field('Issuer', <span className="font-mono text-xs">{cred.issuer}{issuerName ? ` (${issuerName})` : ''}</span>)}
          {field('Subject DID', <span className="font-mono text-xs">{subjectDid}</span>)}
          {field('Issued', formatDate(vc.issuanceDate ?? ''))}
          {field('Expires', vc.expirationDate ? formatDate(vc.expirationDate) : 'Never')}
          {vc.credentialSchema?.id && field('Schema', <span className="font-mono text-xs">{vc.credentialSchema.id}</span>)}
        </div>

        {/* Claims */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-text-faint">Claims</p>
          {claims.length === 0 ? (
            <p className="text-sm text-text-faint">No claims.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border-subtle">
              <table className="w-full">
                <tbody className="divide-y divide-border-subtle">
                  {claims.map(([k, v]) => (
                    <tr key={k}>
                      <td className="bg-slate-50/60 px-4 py-2 text-xs font-bold text-text-faint w-1/3">{k}</td>
                      <td className="px-4 py-2 text-sm text-text-main break-all">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Raw document */}
        <details className="rounded-xl border border-border-subtle">
          <summary className="cursor-pointer px-4 py-3 text-xs font-bold text-text-faint hover:bg-slate-50">
            Raw credential (JSON)
          </summary>
          <pre className="max-h-72 overflow-auto border-t border-border-subtle bg-slate-900 p-4 font-mono text-xs text-slate-300">
            {JSON.stringify(vc, null, 2)}
          </pre>
        </details>
      </div>
    </Modal>
  );
}

export default function MyCredentials() {
  const { t } = useTranslation('common');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'revoked' | 'expired'>('all');
  const [viewing, setViewing] = useState<CredRow | null>(null);
  const [rows, setRows] = useState<CredRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { data: myDIDs } = useMyDIDs();

  // A holder may control several DIDs, and a credential is issued to one of
  // them. Querying only the first active DID (the previous behaviour) silently
  // hid anything issued to the others — the holder saw a subset with no hint
  // that more existed. Fan out across every DID and merge.
  const didList = (myDIDs ?? []).map((d) => d.did).filter(Boolean);
  const didKey = didList.join(',');

  useEffect(() => {
    if (didList.length === 0) return;
    setLoading(true);
    setError(false);
    let cancelled = false;

    Promise.all(
      didList.map((did) =>
        apiFetch<{ credentials?: RawVC[] } | RawVC[]>(
          `/credentials?subject=${encodeURIComponent(did)}`
        ).then((data) =>
          Array.isArray(data) ? data : (data as { credentials?: RawVC[] }).credentials ?? []
        )
      )
    )
      .then((perDid) => {
        if (cancelled) return;
        // Dedupe by credential id in case a DID appears twice in the list.
        const seen = new Set<string>();
        const merged: RawVC[] = [];
        for (const list of perDid) {
          for (const vc of list) {
            const id = String((vc as { id?: unknown }).id ?? '');
            if (id && seen.has(id)) continue;
            if (id) seen.add(id);
            merged.push(vc);
          }
        }
        setRows(merged.map(mapVC));
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // didKey collapses the id list into a stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didKey]);

  const filtered = rows.filter((c) => statusFilter === 'all' || c.status === statusFilter);
  const activeCount = rows.filter((c) => c.status === 'active').length;
  const revokedCount = rows.filter((c) => c.status === 'revoked').length;
  const expiredCount = rows.filter((c) => c.status === 'expired').length;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">My Credentials</h1>
        <p className="mt-1 text-sm text-text-muted">Verifiable credentials issued to your identity</p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[
          { label: 'Total', value: String(rows.length) },
          { label: 'Active', value: String(activeCount) },
          { label: 'Revoked', value: String(revokedCount) },
          { label: 'Expired', value: String(expiredCount) },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-text-main">{stat.value}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {(['all', 'active', 'revoked', 'expired'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors ${
              statusFilter === s
                ? 'bg-primary text-white'
                : 'bg-slate-100 text-text-faint hover:bg-slate-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-500">{t('errors.generic')}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-text-faint">
            <span className="material-symbols-outlined text-4xl">workspace_premium</span>
            <p className="text-sm font-medium">No credentials found</p>
            <p className="text-xs text-text-faint">Credentials issued to your DID will appear here.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle bg-slate-50/50">
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">ID</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Type</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Issuer</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Issued</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Status</th>
                <th className="px-6 py-3 text-right text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {filtered.map((cred) => (
                <tr key={cred.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <CopyableValue value={cred.id} />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-text-main">{cred.type}</td>
                  <td className="px-6 py-4">
                    <CopyableValue value={cred.issuer} />
                  </td>
                  <td className="px-6 py-4 text-sm text-text-muted">{formatDate(cred.issuedAt)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold capitalize ${STATUS_COLORS[cred.status] ?? 'bg-slate-100 text-slate-600'}`}
                      title={cred.status === 'revoked' && cred.revocationReason ? `Reason: ${cred.revocationReason}` : undefined}
                    >
                      {cred.status}
                    </span>
                    {cred.status === 'revoked' && cred.revocationReason && (
                      <p className="mt-1 max-w-[220px] truncate text-[11px] text-red-600" title={formatRevocationReason(cred.revocationReason)}>
                        {formatRevocationReason(cred.revocationReason)}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setViewing(cred)}
                      title="View full credential (read-only)"
                      aria-label="View credential details"
                      className="rounded-lg p-1.5 text-text-faint transition-colors hover:bg-slate-100 hover:text-text-main"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>visibility</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {viewing && <CredentialDetails cred={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
